import {
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  Grid,
  Snackbar,
  Alert,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import saveFilledIcon from "../../assets/img/save_filled_icon.svg";

import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import WorkIcon from "@mui/icons-material/Work";
import LanguageIcon from "@mui/icons-material/Language";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import warning_logo from "../../assets/img/warning_icon.svg";
import coin_icon from "../../assets/img/coin_icon.svg";
import target_icon from "../../assets/img/target_icon.svg";
import mode_icon from "../../assets/img/mode_icon.svg";
import { styled } from "@mui/system";
import logo from "../../assets/img/new_logo.svg";
import saveIcon from "../../assets/img/save_icon.svg";
import { useNavigate } from "react-router-dom";
import useUserId from "hooks/useUserId";
import colors from "colors/color";
import expired_icon from "../../assets/img/expired_icon.svg";
import { useSaveStatus } from "views/Offers/hooks/useSavePost";
import { formatTimeAgo } from "utils/dateUtils";
import { useTranslation } from "react-i18next";

const JobCardContainer = styled(Card)(({ theme }) => ({
  border: "1px solid #F0F0F0",
  backgroundColor: "#FFFFFF",
  padding: theme.spacing(3),
  borderRadius: 20,
  // minWidth: "100%",
  maxWidth: 450,

  flex: "0 0 auto",
  minHeight: 271,
  maxHeight: "max-content",
  display: "flex",
  flexDirection: "column",
  boxShadow: "none",
  cursor: "default",
  transition: "all 0.8s ease",
  "&:hover": {
    boxShadow: "0px 4px 20px rgba(0, 23, 231, 0.1)",
    borderColor: "#0017E7",
    transition: "all 0.3s ease",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    minHeight: "auto",
  },
}));

const CompanyLogo = styled(Box)(({ theme, logo }) => ({
  minWidth: "59px",
  minHeight: "59px",
  maxWidth: "59px",
  maxHeight: "59px",
  background:
    "linear-gradient(135deg, rgba(0, 23, 231, 0.5) -54.69%, rgba(255, 204, 248, 0.5) 66.41%)",
  borderRadius: 14,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff",
  fontSize: "0.75rem",
  textAlign: "center",
  overflow: "hidden",
  [theme.breakpoints.down("sm")]: {
    minWidth: "45px",
    minHeight: "45px",
    maxWidth: "45px",
    maxHeight: "45px",
    borderRadius: 10,
  },
}));

const ExpiryChip = styled(Box)(({ theme }) => ({
  backgroundColor: "#FBBC0533",
  color: "#FBBC05",
  padding: "5px 15px 5px 15px",
  borderRadius: "8px",
  fontSize: "8px",
  fontWeight: 500,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  border: "0.3px solid #FBBC05",
  minWidth: "120px",
  justifyContent: "center",
  whiteSpace: "nowrap",
  maxHeight: 25,
  [theme.breakpoints.down("sm")]: {
    minWidth: "auto",
    padding: "4px 8px",
    fontSize: "7px",
  },
}));

const DetailItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  fontSize: "12px",
  fontWeight: 500,
  fontFamily: "Poppins",
  [theme.breakpoints.down("sm")]: {
    fontSize: "10px",
    gap: theme.spacing(0.5),
  },
}));

const SaveButton = styled(Button)(({ theme, variant, issaved }) => ({
  maxWidth: "100%",
  width: "100%",
  height: 44,
  borderRadius: "11px",
  borderWidth: "1px",
  border: "1px solid #0017E7",
  color: "#0017E7",
  backgroundColor: issaved === "true" ? "#0017E7" : "transparent",
  "&:hover": {
    backgroundColor: issaved === "true" ? "#0017E7" : "rgba(61, 90, 254, 0.04)",
    borderColor: "#3D5AFE",
  },
  padding: "0",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  [theme.breakpoints.down("sm")]: {
    height: 38,
    borderRadius: "8px",
  },
}));

const ActionButton = styled(Button)(({ theme, variant }) => ({
  borderRadius: 11,
  padding: theme.spacing("12px", "24px", "12px", "24px"),
  textTransform: "none",
  fontWeight: 400,
  fontFamily: "Poppins",
  border: variant === "outlined" ? "1px solid #0017E7" : "none",
  color: variant === "outlined" ? "#0017E7" : "#fff",
  backgroundColor: variant === "outlined" ? "transparent" : "#0017E7",
  "&:hover": {
    backgroundColor:
      variant === "outlined" ? "rgba(61, 90, 254, 0.04)" : "#0017E7",
    borderColor: variant === "outlined" ? "#3D5AFE" : "none",
  },
  width: "100%",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing("8px", "16px"),
    fontSize: "12px",
    borderRadius: 8,
    whiteSpace: "nowrap",
  },
  [theme.breakpoints.down("xs")]: {
    padding: theme.spacing("6px", "12px"),
    fontSize: "11px",
  },
}));

const TruncatedTypography = styled(Typography)({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  display: "block",
});

export default function SpontaneousCard({
  spontaneous,
  setSelectedSpontaneous,
  setOpenDialog,
  refresh,
}) {
  const { t: translate } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isXS = useMediaQuery(theme.breakpoints.down("xs"));
  const navigate = useNavigate();
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: "",
    severity: "info",
    action: null,
  });
  const user = useUserId();
  const userId = user.userId;

  const spontaneousId = spontaneous?.id || "";

  const { isLoading, error, toggleSaveStatus, isSaved } = useSaveStatus({
    jobId: spontaneousId,
    type: "spontaneous",
    onSaveChange: (newStatus) => {
      if (spontaneous) {
        console.log(
          `Spontaneous application ${spontaneousId} is now ${
            newStatus ? "saved" : "unsaved"
          }`
        );
      }
    },
  });

  const [deadlineInfo, setDeadlineInfo] = useState({
    color: "#FBBC05", // Default orange
    message: "Days to expire",
    daysRemaining: 0,
    icon: "",
  });

  useEffect(() => {
    console.log("Spontaneous application is : ", spontaneous);
  }, []);

  useEffect(() => {
    const calculateTimeRemaining = () => {
      if (!spontaneous || !spontaneous.deadline) {
        return { color: "#FBBC05", message: "Deadline not set", value: 0 };
      }

      let deadlineDate = new Date(spontaneous.deadline);
      if (isNaN(deadlineDate.getTime())) {
        return { color: "#FBBC05", message: "Invalid date", value: 0 };
      }

      const now = new Date();
      const diffMs = deadlineDate - now;
      const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

      // Expired
      if (days < 0) {
        return {
          color: "#FF0000",
          message: "Expired",
          value: 0,
          icon: expired_icon,
        };
      }

      // Under a week: show days
      if (days < 7) {
        return {
          color: "#FF0000",
          message: `${days} day${days > 1 ? "s" : ""} to expire`,
          value: days,
          icon: warning_logo,
        };
      }

      // Under a month: show weeks
      if (days < 30) {
        const weeks = Math.ceil(days / 7);
        return {
          color: colors.primary,
          message: `${weeks} week${weeks > 1 ? "s" : ""} to expire`,
          value: weeks,
        };
      }

      // A month or more: show months
      const months = Math.ceil(days / 30);
      return {
        color: colors.primary,
        message: `${months} month${months > 1 ? "s" : ""} to expire`,
        value: months,
      };
    };

    if (spontaneous) {
      setDeadlineInfo(calculateTimeRemaining());
    }
  }, [spontaneous?.deadline]);

  useEffect(() => {
    console.log("user id is : ", user);
  }, []);

  // Function to translate - replace with your actual translation implementation
  const t = (key) => {
    const translations = {
      "myCard.unsaveConfirmation": "Do you want to unsave this post?",
      "myCard.yes": "Yes",
      "myCard.no": "No",
      "myCard.postSaved": "Post saved successfully",
    };
    return translations[key] || key;
  };

  const openDetailsDialog = () => {
    setSelectedSpontaneous(spontaneous);
    setOpenDialog(true);
  };

  const apiUrl = process.env.REACT_APP_API_URL;

  const handleApply = () => {
    navigate(`/candidature-spontanee/${spontaneous.admin_details.company}`);
  };

  const handleSaveIconClick = async () => {
    if (isSaved) {
      setSnackbarState({
        open: true,
        message: t("myCard.unsaveConfirmation"),
        severity: "warning",
        action: (
          <>
            <Button
              color="primary"
              size="small"
              onClick={() => {
                toggleSaveStatus();
                setSnackbarState((prev) => ({ ...prev, open: false }));
              }}
              sx={{ color: "#1976d2", mr: 1 }}
            >
              {t("myCard.yes")}
            </Button>
            <Button
              color="secondary"
              size="small"
              onClick={() =>
                setSnackbarState((prev) => ({ ...prev, open: false }))
              }
              sx={{ color: "#666" }}
            >
              {t("myCard.no")}
            </Button>
          </>
        ),
      });
    } else {
      toggleSaveStatus();
      setSnackbarState({
        open: true,
        message: t("myCard.postSaved"),
        severity: "success",
        action: null,
      });
      setTimeout(() => {
        setSnackbarState((prev) => ({ ...prev, open: false }));
      }, 2000);
    }
  };

  return (
    <>
      <JobCardContainer>
        <CardContent
          sx={{
            padding: 0,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            "&:last-child": { paddingBottom: 0 },
          }}
        >
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                gap: isMobile ? 1 : 2,
                mb: isMobile ? 1.5 : 2,
              }}
            >
              <CompanyLogo
                logo={spontaneous?.admin_details?.company_logo || logo}
              >
                <img
                  src={spontaneous?.admin_details?.company_logo || logo}
                  alt="Company Logo"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </CompanyLogo>
              <Box sx={{ width: "100%", overflow: "hidden" }}>
                {/* First line: Title and Expiry Chip */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: isMobile ? 0.5 : 1.5,
                    width: "100%",
                    flexWrap: isMobile ? "wrap" : "nowrap",
                    gap: isMobile ? 0.5 : 0,
                  }}
                >
                  <TruncatedTypography
                    variant={isMobile ? "subtitle1" : "h6"}
                    sx={{
                      fontWeight: 600,
                      fontSize: isMobile ? "14px" : "16px",
                      fontFamily: "Poppins",
                      textTransform: "none",
                      maxWidth: isMobile ? "100%" : "160px",
                      mb: isMobile ? 0.5 : 0,
                    }}
                  >
                    {spontaneous.admin_details.company || "NaN"}
                  </TruncatedTypography>
                  {/* <ExpiryChip color={deadlineInfo.color}>
                      <Box
                        component="img"
                        src={deadlineInfo.icon}
                        sx={{
                          width: isMobile ? 10 : 12,
                          height: isMobile ? 10 : 12,
                          display: "inline-flex",
                          paddingBottom: 0.2,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ fontSize: isMobile ? "7px" : "8px" }}
                      >
                        {deadlineInfo.message}
                      </Typography>
                    </ExpiryChip> */}
                </Box>

                {/* Second line: Company and Location with full width */}
                <Box
                  sx={{
                    display: "flex",
                    width: "100%",
                    overflow: "hidden",
                    flexWrap: isMobile && isXS ? "wrap" : "nowrap",
                  }}
                >
                  <TruncatedTypography
                    variant="body2"
                    sx={{
                      color: "#000000",
                      fontWeight: 700,
                      fontFamily: "Poppins",
                      fontSize: isMobile ? 9 : 10,
                      flexShrink: 0,
                    }}
                  >
                    {spontaneous.admin_details.comp_industry || "NaN"}
                  </TruncatedTypography>
                  <TruncatedTypography
                    variant="body2"
                    sx={{
                      color: "#00000080",
                      fontSize: isMobile ? 9 : 10,
                      fontFamily: "Poppins",
                      fontWeight: 300,
                      ml: 0.5,
                      flexGrow: 1,
                    }}
                  >
                    {spontaneous.localisation
                      ? `· ${spontaneous.localisation}`
                      : "· Morocco, Marrakech"}{" "}
                    · {formatTimeAgo(spontaneous.updated_at)}
                  </TruncatedTypography>
                </Box>
              </Box>
            </Box>

            {/* Job Description - with ellipsis for overflow */}
            <Typography
              variant="body2"
              sx={{
                mb: isMobile ? 1.5 : 2,
                color: "#000000B2",
                fontSize: isMobile ? "11px" : "12px",
                lineHeight: isMobile ? "16px" : "18px",
                fontFamily: "Poppins",
                fontWeight: 400,
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
                height: isMobile ? "48px" : "54px", // Adjusted height for mobile
              }}
            >
              {spontaneous.description || "NaN"}
            </Typography>

            {/* Job Details Row */}
            {/* <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: isMobile ? 1 : 2,
                  mb: isMobile ? 1.5 : 2,
                  justifyContent: "flex-start",
                }}
              >
                <DetailItem sx={{ color: "#0017E7" }}>
                  <Box
                    component="img"
                    src={coin_icon}
                    sx={{
                      width: isMobile ? 16 : 20,
                      height: isMobile ? 16 : 20,
                      display: "inline-flex",
                      paddingBottom: 0.2,
                    }}
                  />
                  <TruncatedTypography sx={{ fontWeight: 500 }}>
                    {spontaneous.salary || "NaN"}
                  </TruncatedTypography>
                  <Typography
                    sx={{
                      color: "#00000080",
                      fontWeight: 300,
                      fontSize: isMobile ? "9px" : "inherit",
                    }}
                  >
                    /Month
                  </Typography>
                </DetailItem>
  
                <DetailItem sx={{ color: "#0017E7" }}>
                  <Box
                    component="img"
                    src={target_icon}
                    sx={{
                      width: isMobile ? 16 : 20,
                      height: isMobile ? 16 : 20,
                      display: "inline-flex",
                      paddingBottom: 0.2,
                    }}
                  />
                  <TruncatedTypography>
                    {spontaneous.jobType === "i" ? "Internship" : "Job"}
                  </TruncatedTypography>
                </DetailItem>
  
                <DetailItem sx={{ color: "#0017E7" }}>
                  <Box
                    component="img"
                    src={mode_icon}
                    sx={{
                      width: isMobile ? 16 : 20,
                      height: isMobile ? 16 : 20,
                      display: "inline-flex",
                      paddingBottom: 0.2,
                    }}
                  />
                  <TruncatedTypography>
                    {spontaneous.mode || "Remote"}
                  </TruncatedTypography>
                </DetailItem>
              </Box> */}
          </Box>

          {/* Action Buttons - placed at the bottom with responsive layout */}
          <Box sx={{ mt: "auto" }}>
            {/* On mobile view */}
            {isMobile && (
              <>
                <Grid container spacing={1} sx={{ mb: 1 }}>
                  {userId && user.role === "c" && (
                    <Grid item xs={12}>
                      <SaveButton
                        variant="outlined"
                        issaved={isSaved ? "true" : "false"}
                        onClick={handleSaveIconClick}
                        sx={{ mb: 1 }}
                      >
                        <img
                          src={!isSaved ? `${saveIcon}` : `${saveFilledIcon}`}
                          width={10}
                          height={16}
                          style={{
                            filter: isSaved
                              ? "brightness(0) invert(1)"
                              : "none",
                          }}
                        />
                      </SaveButton>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <ActionButton
                      variant="outlined"
                      onClick={handleApply}
                      fullWidth
                    >
                      <TruncatedTypography>
                        {translate("apply_now")}
                      </TruncatedTypography>
                    </ActionButton>
                  </Grid>
                </Grid>
              </>
            )}

            {/* On desktop view */}
            {!isMobile && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
                    <Box sx={{ flex: 1 }}>
                      <ActionButton variant="outlined" onClick={handleApply}>
                        <TruncatedTypography>
                          {translate("apply_now")}
                        </TruncatedTypography>
                      </ActionButton>
                    </Box>

                    {userId && user.role === "c" && (
                      <SaveButton
                        variant="outlined"
                        issaved={isSaved ? "true" : "false"}
                        onClick={handleSaveIconClick}
                        sx={{
                          width: "auto",
                          minWidth: "44px",
                          maxWidth: "44px",
                        }}
                      >
                        <img
                          src={!isSaved ? `${saveIcon}` : `${saveFilledIcon}`}
                          width={10}
                          height={16}
                          style={{
                            filter: isSaved
                              ? "brightness(0) invert(1)"
                              : "none",
                          }}
                        />
                      </SaveButton>
                    )}
                  </Box>
                </Grid>
              </Grid>
            )}
          </Box>
          {/* </Grid> */}
        </CardContent>
      </JobCardContainer>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarState.open}
        autoHideDuration={snackbarState.action ? null : 3000}
        onClose={() => setSnackbarState((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: isMobile ? "center" : "center",
        }}
        sx={{
          bottom: isMobile ? "24px !important" : undefined,
          width: isMobile ? "90%" : undefined,
          left: isMobile ? "50% !important" : undefined,
          transform: isMobile ? "translateX(-50%) !important" : undefined,
        }}
      >
        <Alert
          severity={snackbarState.severity}
          action={snackbarState.action}
          sx={{
            width: "100%",
            fontSize: isMobile ? "12px" : undefined,
          }}
        >
          {snackbarState.message}
        </Alert>
      </Snackbar>
    </>
  );
}
